import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  MessageCircle,
  Mic,
  Search,
  Clock,
  User,
  FileText,
  Calendar,
  Activity,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { sessionApi, type Session } from "../services/sessionApi";

import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/Loader";

export function CompanySessionsPage() {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"createdAt" | null>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(true);
  const [sortLoading, setSortLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    if (companyId) {
      loadSessions(true);
    }
  }, [companyId]);

  // Handle sorting changes
  useEffect(() => {
    if (companyId && !loading) {
      loadSessions(false);
    }
  }, [sortBy, sortOrder, companyId, loading]);

  useEffect(() => {
    filterSessions();
  }, [sessions, searchTerm, statusFilter, typeFilter]);

  const loadSessions = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSortLoading(true);
      }
      setError(null);
      if (!companyId) return;

      // Prepare sorting parameters - always default to createdAt desc if no sorting is specified
      const queryParams = {
        sortBy: sortBy || "createdAt",
        sortOrder: sortBy ? sortOrder : "desc",
      };

      const data = await sessionApi.getSessionsByCompany(
        companyId,
        queryParams
      );
      setSessions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load sessions");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setSortLoading(false);
      }
    }
  };

  const filterSessions = () => {
    let filtered = sessions;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (session) =>
          session.fromNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          session.user?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          session.user?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          session.user?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          session.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((session) => session.status === statusFilter);
    }

    // Filter by type
    if (typeFilter !== "all") {
      filtered = filtered.filter((session) => session.type === typeFilter);
    }

    // Note: Sorting is now handled by the backend, not here

    setFilteredSessions(filtered);
  };

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case "call":
        return <Phone className="h-4 w-4" />;
      case "web_voice":
        return <Mic className="h-4 w-4" />;
      case "web_chat":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      ACTIVE: "bg-green-100 text-green-800",
      COMPLETED: "bg-blue-100 text-blue-800",
      CANCELLED: "bg-red-100 text-red-800",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800"
        }
      >
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (milliseconds?: number) => {
    if (!milliseconds) return "-";
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
  };

  const handleSortByCreated = () => {
    if (sortBy === "createdAt") {
      // If already sorting by created date, toggle order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // If not sorting by created date, start with descending order
      setSortBy("createdAt");
      setSortOrder("desc");
    }
  };

  const getSortIcon = () => {
    if (sortBy !== "createdAt") return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p>Company ID not provided</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {sessions[0]?.company?.name} Sessions
          </h1>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sessions ({filteredSessions.length})</CardTitle>
            <div className="flex items-center space-x-4">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="call">Phone Calls</SelectItem>
                  <SelectItem value="web_voice">Web Voice</SelectItem>
                  <SelectItem value="web_chat">Web Chat</SelectItem>
                </SelectContent>
              </Select>

              {/* Search */}
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search sessions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {sortLoading && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Sorting...</span>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className={`cursor-pointer hover:bg-gray-50 select-none ${
                      sortLoading ? "pointer-events-none opacity-50" : ""
                    }`}
                    onClick={handleSortByCreated}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {getSortIcon()}
                    </div>
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>User/Phone</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Transcripts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSessions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-gray-500"
                    >
                      {searchTerm ||
                      statusFilter !== "all" ||
                      typeFilter !== "all"
                        ? "No sessions found matching your filters."
                        : "No sessions found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSessions.map((session) => (
                    <TableRow
                      key={session.id}
                      onClick={() => {
                        navigate(`/admin/sessions/${session.id}`);
                      }}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <div>
                            <div>{formatDate(session.createdAt)}</div>
                            <div className="text-xs text-gray-500">
                              {formatDateTime(session.createdAt).split(",")[1]}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getSessionTypeIcon(session.type)}
                          <span>{session.type.replace("_", " ")}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(session.status)}</TableCell>
                      <TableCell>
                        {session.user ? (
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="font-medium">
                                {session.user.firstName} {session.user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {session.user.email}
                              </div>
                            </div>
                          </div>
                        ) : session.fromNumber ? (
                          <div className="flex items-center space-x-2">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span>
                              {session.fromNumber} to{" "}
                              {session.toNumber?.e164Number}
                            </span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span>{formatDuration(session.duration)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <FileText className="h-3 w-3 text-gray-400" />
                          <span>{session._count?.transcripts || 0}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
